import React, { useEffect } from 'react'
import { Navigate } from 'react-router-dom'

import { displayErrorMessage } from '@harvey-frontend/utils/toast'
import * as Sentry from '@sentry/react'
import { useShallow } from 'zustand/react/shallow'

import Services from 'services'

import { setDatadogState } from 'utils/datadog'

import Error from '@harvey-frontend/components/common/error/error'
import LoadingScreen from 'components/common/loading-screen'

import { validateUUID } from '../../services/document/utilities'
import { useAccessTokenContext } from './providers/AccessTokenProvider'
import useAppStore from './stores/store'
import {
  getCustomProperty,
  getFileUrl,
  setCustomProperty,
} from './utils/office'

export const Main = () => {
  const { isAuthenticated, userInfo, logoutHandler } = useAccessTokenContext()
  const { documentId, eventId } = useAppStore(
    useShallow((state) => ({
      documentId: state.documentId,
      eventId: state.eventId,
    }))
  )

  const { setDocumentId, setEventId } = useAppStore(
    useShallow((state) => ({
      setDocumentId: state.setDocumentId,
      setEventId: state.setEventId,
    }))
  )

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    const fetchDocumentId = async () => {
      const documentId = await getCustomProperty('HarveyDocumentId')
      if (documentId && validateUUID(documentId)) {
        setDocumentId(documentId)
        try {
          const { eventId } = await Services.Backend.Get<{
            eventId: number
          }>(`drafting/word_documents/${documentId}`, {
            throwOnError: true,
          })
          setEventId(eventId)
          return
        } catch (e) {
          // This is a fallback for when the document is not found.
          // Shouldn't happen in normal use cases, but will prevent us from getting stuck in a completely unusable state.
          console.error(e)
          Sentry.captureException(e)
        }
      }

      let filename = ''
      try {
        filename = await getFileUrl()
      } catch (e) {
        displayErrorMessage('Error getting filename while setting up document')
        Sentry.captureException(e)
        return
      }

      const { id, eventId } = await Services.Backend.Post<{
        id: string
        eventId: number
      }>(
        'drafting/word_documents',
        {
          filename: filename,
        },
        { throwOnError: true }
      ).catch((e) => {
        displayErrorMessage('Error setting up document')
        throw e
      })

      await setCustomProperty('HarveyDocumentId', id)
      setDocumentId(id)
      setEventId(eventId)
    }

    void fetchDocumentId()
  }, [isAuthenticated, setDocumentId, setEventId])

  useEffect(() => {
    Sentry.setUser({
      username: userInfo?.nonDbId,
    })
    setDatadogState(userInfo)
  }, [userInfo])

  if (!userInfo?.isWordAddInUser) {
    return (
      <Error
        primaryCTA={{ text: 'Logout', onClick: logoutHandler }}
        description="This is likely caused by your account not having proper access to the Harvey platform. Please contact your internal Harvey administrator for assistance. If this issue persists and more help is needed, contact support@harvey.ai."
      />
    )
  }

  if (!documentId || !eventId) {
    return <LoadingScreen />
  }

  // Navigate to app selection if user is a playbook user.
  if (userInfo?.isPlaybookReviewUser) {
    return <Navigate to="/app-selection" />
  }

  // Otherwise, navigate directly to document edit screen.
  return <Navigate to="/document-edit" />
}

export default Main
